import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { PermitStatusService } from '../../services/permit-status.service';
import { PermitSettingsService } from '../../services/permit-settings.service';
import { MessageService } from '../../../global/services/message/message.abstract.service';
import { BcitiConfirmDialogComponent } from '../../../global/components/dialogs/bciti-confirm-dialog/bciti-confirm-dialog.component';
import { PermitStatus } from '../../models/permit-status.model';
import { PermitStatusMigrateDialogComponent } from '../dialogs/permit-status-migrate-dialog/permit-status-migrate-dialog.component';

@Component({
    selector: 'app-permit-config-statuses',
    templateUrl: './permit-config-statuses.component.html',
    styleUrls: ['./permit-config-statuses.component.scss'],
})
export class PermitConfigStatusesComponent implements OnInit, OnDestroy {
    @Output() create = new EventEmitter<void>();
    @Output() edit = new EventEmitter<PermitStatus>();

    defaultStatuses: PermitStatus[] = [];
    customStatuses: PermitStatus[] = [];
    loading = true;

    // The external system owns the workflow when the org is connected: statuses become read-only.
    locked = false;

    private destroy$ = new Subject<void>();

    constructor(
        private statusService: PermitStatusService,
        private settingsService: PermitSettingsService,
        private dialog: MatDialog,
        private translate: TranslateService,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.loadStatuses();
        this.settingsService.isExternallyManaged$.pipe(takeUntil(this.destroy$)).subscribe((connected) => {
            this.locked = connected;
        });
    }

    loadStatuses(): void {
        this.loading = true;
        forkJoin([this.statusService.getDefaultStatuses(), this.statusService.getCreatedStatuses()])
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ([defaults, created]) => {
                    this.defaultStatuses = (defaults || []).slice().sort((a, b) => a.displayOrder - b.displayOrder);
                    this.customStatuses = (created || []).slice().sort((a, b) => a.displayOrder - b.displayOrder);
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                    this.messageService.error(this.translate.instant('permit.config.common.error'));
                },
            });
    }

    statusName(status: PermitStatus): string {
        const { name } = status;
        if (!name) return '';
        if (typeof name === 'string') return name;
        const lang = this.translate.currentLang || 'fr';
        return name[lang] || name.fr || name.en || '';
    }

    onEdit(status: PermitStatus): void {
        if (this.locked) return;
        this.edit.emit(status);
    }

    onCreate(): void {
        if (this.locked) return;
        this.create.emit();
    }

    confirmDelete(status: PermitStatus): void {
        if (this.locked) return;
        const dialogRef = this.dialog.open(BcitiConfirmDialogComponent, {
            width: '400px',
            data: {
                dialog: {
                    title: this.translate.instant('permit.config.statuses.delete_title'),
                    body: this.translate.instant('permit.config.statuses.delete_body'),
                    confirmLabel: this.translate.instant('permit.config.common.delete'),
                    cancelLabel: this.translate.instant('permit.config.common.cancel'),
                    confirmButtonClass: 'danger-btn',
                },
                component: { data: {}, entry: null },
            },
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((confirmed) => {
                if (!confirmed) return;
                this.statusService
                    .deleteStatus(status._id)
                    .pipe(takeUntil(this.destroy$))
                    .subscribe({
                        next: () => this.loadStatuses(),
                        error: () => this.openMigrateDialog(status),
                    });
            });
    }

    private openMigrateDialog(source: PermitStatus): void {
        const targets = [...this.defaultStatuses, ...this.customStatuses].filter((s) => s._id !== source._id);
        const dialogRef = this.dialog.open(PermitStatusMigrateDialogComponent, {
            width: '480px',
            data: { source, targets },
        });

        dialogRef
            .afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe((targetId: string | undefined) => {
                if (!targetId) return;
                this.statusService
                    .migrateStatus(source._id, { targetStatus: targetId })
                    .pipe(
                        switchMap(() => this.statusService.deleteStatus(source._id)),
                        takeUntil(this.destroy$),
                    )
                    .subscribe({
                        next: () => this.loadStatuses(),
                        error: () => this.messageService.error(this.translate.instant('permit.config.common.error')),
                    });
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
